import { useEffect, useState } from 'react';

const RADIO_NOWPLAYING_API = 'https://radio.minibid.org/api/nowplaying/1';
const DEFAULT_INTERVAL_MS = 5000;

export type RadioNowPlayingSnapshot = {
  title: string;
  artist: string;
  art: string;
  listeners: number;
  isLive: boolean;
  streamerName: string;
  elapsedSec: number;
  durationSec: number;
  remainingSec: number;
  fetchedAt: number;
};

let snapshot: RadioNowPlayingSnapshot | null = null;
let inFlight: Promise<void> | null = null;
let intervalId: number | null = null;
let listenersBound = false;
const subscribers = new Set<() => void>();

const emit = () => {
  subscribers.forEach((cb) => cb());
};

const toReadableWords = (value: string) => {
  return value
    .replace(/[_]+/g, ' ')
    .replace(/[-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const decodeSlugArtistTitle = (value: string): { artist: string; title: string } => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return { artist: '', title: '' };

  // Common AzuraCast/automation pattern: artist-title-123456
  const withoutNumericTail = normalized.replace(/-\d{4,}$/g, '');
  const parts = withoutNumericTail.split('-').filter(Boolean);

  if (parts.length <= 1) {
    return { artist: '', title: toReadableWords(withoutNumericTail) };
  }

  const artist = toReadableWords(parts[0]);
  const title = toReadableWords(parts.slice(1).join('-'));
  return { artist, title };
};

const parseSnapshot = (payload: any, prev: RadioNowPlayingSnapshot | null): RadioNowPlayingSnapshot => {
  const song = payload?.now_playing?.song || {};
  const rawText = String(song?.text || '').trim();
  const splitText = rawText.includes(' - ') ? rawText.split(' - ') : [];
  const textArtist = splitText.length > 1 ? String(splitText[0] || '').trim() : '';
  const textTitle = splitText.length > 1 ? String(splitText.slice(1).join(' - ') || '').trim() : rawText;

  const rawSongTitle = String(song?.title || '').trim();
  const rawSongArtist = String(song?.artist || '').trim();
  const decodedFromSongTitle = decodeSlugArtistTitle(rawSongTitle);
  const decodedFromText = decodeSlugArtistTitle(rawText);

  const nextTitle =
    rawSongTitle ||
    textTitle ||
    decodedFromSongTitle.title ||
    decodedFromText.title ||
    prev?.title ||
    '';

  const nextArtist =
    rawSongArtist ||
    textArtist ||
    decodedFromSongTitle.artist ||
    decodedFromText.artist ||
    prev?.artist ||
    '';

  const normalizedTitle = toReadableWords(nextTitle);
  const normalizedArtist = toReadableWords(nextArtist);

  return {
    title: normalizedTitle,
    artist: normalizedArtist,
    art: String(song?.art || '').trim() || prev?.art || '',
    listeners: Number(payload?.listeners?.total || 0),
    isLive: Boolean(payload?.live?.is_live),
    streamerName: String(payload?.live?.is_live ? payload?.live?.streamer_name || '' : 'AutoDJ').trim() || 'AutoDJ',
    elapsedSec: Number(payload?.now_playing?.elapsed ?? prev?.elapsedSec ?? 0),
    durationSec: Number(payload?.now_playing?.duration ?? prev?.durationSec ?? 0),
    remainingSec: Number(payload?.now_playing?.remaining ?? prev?.remainingSec ?? 0),
    fetchedAt: Date.now(),
  };
};

const hasMeaningfulChange = (
  prev: RadioNowPlayingSnapshot | null,
  next: RadioNowPlayingSnapshot,
) => {
  if (!prev) return true;
  return (
    prev.title !== next.title ||
    prev.artist !== next.artist ||
    prev.art !== next.art ||
    prev.listeners !== next.listeners ||
    prev.isLive !== next.isLive ||
    prev.streamerName !== next.streamerName ||
    prev.elapsedSec !== next.elapsedSec ||
    prev.durationSec !== next.durationSec ||
    prev.remainingSec !== next.remainingSec
  );
};

const fetchNowPlaying = async () => {
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      const separator = RADIO_NOWPLAYING_API.includes('?') ? '&' : '?';
      const response = await fetch(`${RADIO_NOWPLAYING_API}${separator}t=${Date.now()}`, {
        cache: 'no-store',
      });
      if (!response.ok) return;

      const data = await response.json();
      const nextSnapshot = parseSnapshot(data, snapshot);

      if (hasMeaningfulChange(snapshot, nextSnapshot)) {
        snapshot = nextSnapshot;
        emit();
      } else {
        // Keep fetchedAt fresh so consumers can infer update freshness if needed.
        snapshot = { ...nextSnapshot };
      }
    } catch {
      // Ignore transient network errors.
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
};

const bindWindowListeners = () => {
  if (listenersBound || typeof window === 'undefined') return;

  const refresh = () => {
    void fetchNowPlaying();
  };

  window.addEventListener('focus', refresh);
  window.addEventListener('online', refresh);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      refresh();
    }
  });

  listenersBound = true;
};

const ensurePolling = () => {
  if (intervalId !== null || typeof window === 'undefined') return;
  bindWindowListeners();
  intervalId = window.setInterval(() => {
    void fetchNowPlaying();
  }, DEFAULT_INTERVAL_MS);
};

const stopPollingIfIdle = () => {
  if (subscribers.size > 0 || intervalId === null || typeof window === 'undefined') return;
  window.clearInterval(intervalId);
  intervalId = null;
};

type UseRadioNowPlayingOptions = {
  enabled?: boolean;
};

export function useRadioNowPlaying(options?: UseRadioNowPlayingOptions) {
  const enabled = options?.enabled ?? true;
  const [state, setState] = useState<RadioNowPlayingSnapshot | null>(snapshot);

  useEffect(() => {
    if (!enabled) return;

    const notify = () => setState(snapshot);
    subscribers.add(notify);
    ensurePolling();

    // Immediate fetch on subscription for fastest first render.
    void fetchNowPlaying().then(() => {
      setState(snapshot);
    });

    return () => {
      subscribers.delete(notify);
      stopPollingIfIdle();
    };
  }, [enabled]);

  return {
    nowPlaying: state,
    refreshNowPlaying: () => fetchNowPlaying(),
  };
}
